import { useCallback, useEffect, useMemo, useRef } from 'react';

import { DEFAULT_ROWS_PER_PAGE } from '@/constant';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import { useSocket } from './useSocket';
import { fetchNotifications, markNotificationAsRead } from '@/store/slices/notifications';
import type { NotificationStatus } from '@/types/notification';
import { shouldEnableNotifications } from '@/utils/role';

export type NotificationListFilter = 'all' | NotificationStatus;

interface UseNotificationsOptions {
    enabled?: boolean;
    statusFilter?: NotificationListFilter;
    onMarkedAsRead?: () => void;
}

const toApiStatus = (filter: NotificationListFilter): NotificationStatus | undefined => {
    return filter === 'all' ? undefined : filter;
};

export const useNotifications = ({ enabled = false, statusFilter = 'all', onMarkedAsRead }: UseNotificationsOptions = {}) => {
    const dispatch = useAppDispatch();
    const { currentHospitalId } = useAppSelector(({ hospital }) => hospital);
    const { profile } = useAppSelector(({ profile: profileState }) => profileState);
    const { items, total, page, loading, loadingMore, error, markingAsReadId } = useAppSelector(({ notifications }) => notifications);
    const notificationsEnabled = shouldEnableNotifications(profile);
    const { socket } = useSocket({ enabled: notificationsEnabled });
    const activeRequestRef = useRef<{ abort?: () => void } | null>(null);
    const loadMoreRequestRef = useRef<{ abort?: () => void } | null>(null);

    const loadNotifications = useCallback(() => {
        activeRequestRef.current?.abort?.();
        loadMoreRequestRef.current?.abort?.();

        const request = dispatch(
            fetchNotifications({
                status: toApiStatus(statusFilter),
                page: 1,
                limit: DEFAULT_ROWS_PER_PAGE,
                hospitalId: currentHospitalId ?? undefined
            })
        );
        activeRequestRef.current = request;
        return request;
    }, [dispatch, statusFilter, currentHospitalId]);

    const loadMore = useCallback(() => {
        if (loadingMore || items.length >= total) {
            return undefined;
        }

        loadMoreRequestRef.current?.abort?.();
        const request = dispatch(
            fetchNotifications({
                status: toApiStatus(statusFilter),
                page: page + 1,
                limit: DEFAULT_ROWS_PER_PAGE,
                hospitalId: currentHospitalId ?? undefined,
                append: true
            })
        );
        loadMoreRequestRef.current = request;
        return request;
    }, [dispatch, statusFilter, currentHospitalId, loadingMore, items.length, total, page]);

    const hasMore = useMemo(() => items.length < total, [items.length, total]);

    useEffect(() => {
        if (!enabled || !notificationsEnabled) {
            return undefined;
        }

        loadNotifications();

        return () => {
            activeRequestRef.current?.abort?.();
            activeRequestRef.current = null;
            loadMoreRequestRef.current?.abort?.();
            loadMoreRequestRef.current = null;
        };
    }, [enabled, notificationsEnabled, loadNotifications]);

    useEffect(() => {
        if (!enabled || !notificationsEnabled || !socket) {
            return undefined;
        }

        const onCreated = () => {
            loadNotifications();
        };

        socket.on('notification.created', onCreated);

        return () => {
            socket.off('notification.created', onCreated);
        };
    }, [enabled, notificationsEnabled, socket, loadNotifications]);

    const handleMarkAsRead = useCallback(
        async (id: string) => {
            try {
                await dispatch(
                    markNotificationAsRead({
                        id,
                        hospitalId: currentHospitalId ?? undefined
                    })
                ).unwrap();
                onMarkedAsRead?.();
            } catch {
                // Error is stored in notifications.markAsReadError
            }
        },
        [dispatch, currentHospitalId, onMarkedAsRead]
    );

    return {
        items,
        total,
        loading,
        loadingMore,
        hasMore,
        error,
        markingAsReadId,
        loadNotifications,
        loadMore,
        handleMarkAsRead
    };
};
